import React, { useState } from "react";
import { formatDateTime } from "../../utils/date";

/* ── Inline SVG icons ──────────────────────────────────────────────────── */

const S = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };

const IconEye          = () => <svg width={13} height={13} viewBox="0 0 24 24" {...S} aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const IconTag          = () => <svg width={13} height={13} viewBox="0 0 24 24" {...S} aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;
const IconPackage      = () => <svg width={13} height={13} viewBox="0 0 24 24" {...S} aria-hidden="true"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
const IconCart         = () => <svg width={13} height={13} viewBox="0 0 24 24" {...S} aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>;
const IconCheck        = () => <svg width={13} height={13} viewBox="0 0 24 24" {...S} aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>;
const IconLock         = () => <svg width={13} height={13} viewBox="0 0 24 24" {...S} aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>;
const IconUserPlus     = () => <svg width={13} height={13} viewBox="0 0 24 24" {...S} aria-hidden="true"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>;
const IconMapPin       = () => <svg width={13} height={13} viewBox="0 0 24 24" {...S} aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const IconCircleCheck  = ({ size = 10 }) => <svg width={size} height={size} viewBox="0 0 24 24" {...S} aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>;
const IconChevronDown  = () => <svg width={14} height={14} viewBox="0 0 24 24" {...S} aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>;
const IconCreditCard   = () => <svg width={14} height={14} viewBox="0 0 24 24" {...S} aria-hidden="true"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;

/* ── Event config ──────────────────────────────────────────────────────── */

const EVENT_CONFIG = {
    page_view:        { Icon: IconEye,       bg: "bg-gray-100",    text: "text-gray-500",   label: "Page view" },
    pricing_view:     { Icon: IconTag,       bg: "bg-amber-50",    text: "text-amber-600",  label: "Pricing view" },
    plan_clicked:     { Icon: IconPackage,   bg: "bg-indigo-50",   text: "text-indigo-600", label: "Plan clicked" },
    checkout_started: { Icon: IconCart,      bg: "bg-orange-50",   text: "text-orange-600", label: "Checkout started" },
    payment_success:  { Icon: IconCheck,     bg: "bg-green-50",    text: "text-green-600",  label: "Payment success" },
    login:            { Icon: IconLock,      bg: "bg-blue-50",     text: "text-blue-600",   label: "Login" },
    register:         { Icon: IconUserPlus,  bg: "bg-purple-50",   text: "text-purple-600", label: "Register" },
};

const DEFAULT_CONFIG = { Icon: IconMapPin, bg: "bg-gray-100", text: "text-gray-400", label: null };

/* ── Sub-components ───────────────────────────────────────────────────── */

const EventRow = ({ event, isLast }) => {
    const cfg = EVENT_CONFIG[event.event_name] || DEFAULT_CONFIG;
    const label = cfg.label || event.event_name.replaceAll("_", " ");

    return (
        <div className="flex gap-3">
            <div className="flex flex-col items-center">
                <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${cfg.bg} ${cfg.text}`}>
                    <cfg.Icon />
                </div>
                {!isLast && <div className="mt-1 w-px flex-1 bg-gray-100" />}
            </div>
            <div className="pb-4">
                <div className="text-[13px] font-medium capitalize text-gray-800">{label}</div>
                {event.page_path && (
                    <div className="mt-0.5 max-w-xs truncate text-[11px] text-gray-400">
                        {event.page_path}
                    </div>
                )}
                <div className="mt-0.5 text-[11px] text-gray-400">
                    {formatDateTime(event.created_at)}
                </div>
            </div>
        </div>
    );
};

const InfoGrid = ({ session }) => (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3 border-b border-gray-100 px-5 py-4">
        {[
            { label: "Landing page", value: session.landing_path },
            { label: "Referrer",     value: session.referrer },
            { label: "Campaign",     value: session.utm_campaign },
            { label: "Source",       value: session.utm_source },
        ].map(({ label, value }) => (
            <div key={label}>
                <div className="text-[10px] uppercase tracking-widest text-gray-400">{label}</div>
                <div className="mt-0.5 truncate text-[12px] font-medium text-gray-700">
                    {value || "—"}
                </div>
            </div>
        ))}
    </div>
);

const SessionPayment = ({ payment }) => (
    <div className="flex items-center justify-between rounded-lg border border-green-100 bg-white px-4 py-3">
        <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-green-50 text-green-600">
                <IconCreditCard />
            </div>
            <div>
                <div className="text-[12px] font-medium text-green-900">{payment.product}</div>
                <div className="text-[11px] text-green-700">{payment.gateway}</div>
            </div>
        </div>
        <div className="text-right">
            <div className="text-[14px] font-medium text-green-700">
                ${Number(payment.amount).toLocaleString("en-IN")}
            </div>
            <div className="text-[11px] text-green-600">{payment.status}</div>
        </div>
    </div>
);

/* ── Main component ───────────────────────────────────────────────────── */

const AnalyticsSessionTimeline = ({ sessions = [] }) => {
    const [expanded, setExpanded] = useState([0]);

    const toggle = (i) =>
        setExpanded((prev) =>
            prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
        );

    if (!sessions.length) {
        return (
            <div className="rounded-xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-400">
                No sessions recorded.
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {sessions.map((item, index) => {
                const isOpen = expanded.includes(index);
                const hasPayment = item.payments?.length > 0;

                return (
                    <div
                        key={index}
                        className="overflow-hidden rounded-xl border border-gray-200 bg-white"
                    >
                        {/* Accordion header */}
                        <button
                            type="button"
                            onClick={() => toggle(index)}
                            aria-expanded={isOpen}
                            className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition hover:bg-gray-50"
                        >
                            <div className="flex min-w-0 items-center gap-3">
                                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-[11px] font-medium text-gray-500">
                                    {index + 1}
                                </div>
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-[13px] font-medium text-gray-800">
                                            Session #{index + 1}
                                        </span>
                                        <span className="rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                                            {item.events.length} events
                                        </span>
                                        {hasPayment && (
                                            <span className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                                                <IconCircleCheck size={10} />
                                                Converted
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-0.5 truncate text-[11px] text-gray-400">
                                        {[item.session.browser, item.session.device, item.session.utm_source || "Direct"]
                                            .filter(Boolean)
                                            .join(" · ")}
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-shrink-0 items-center gap-4">
                                <div className="text-right">
                                    <div className="text-[10px] uppercase tracking-widest text-gray-400">Started</div>
                                    <div className="text-[12px] font-medium text-gray-700">
                                        {formatDateTime(item.session.started_at)}
                                    </div>
                                </div>
                                <span
                                    className="text-gray-400 transition-transform"
                                    style={{ display: "inline-flex", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                                >
                                    <IconChevronDown />
                                </span>
                            </div>
                        </button>

                        {/* Expanded content */}
                        {isOpen && (
                            <>
                                <InfoGrid session={item.session} />

                                {hasPayment && (
                                    <div className="flex flex-col gap-2 border-b border-gray-100 bg-green-50/40 px-5 py-4">
                                        <div className="mb-1 text-[10px] font-medium uppercase tracking-widest text-green-700">
                                            Payment in this session
                                        </div>
                                        {item.payments.map((p, idx) => (
                                            <SessionPayment key={idx} payment={p} />
                                        ))}
                                    </div>
                                )}

                                <div className="px-5 pb-3 pt-5">
                                    {item.events.map((event, idx) => (
                                        <EventRow
                                            key={idx}
                                            event={event}
                                            isLast={idx === item.events.length - 1}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>                );
            })}
        </div>
    );
};

export default AnalyticsSessionTimeline;